import { hreflangLocales, type PlatformLocale } from '@/i18n/locales'
import { TRANS_MODES } from '@/services/wapi/types/c2c'

import { localeHomeUrl } from './menu-config'

/**
 * The country picker's options — which countries the header offers, and where each
 * one sends the visitor from the page they are on.
 *
 * **Country, not language.** The control is a flag and a place name because that is
 * what a visitor is choosing: the market they are shopping in (routes, carriers,
 * currency), of which the language is a consequence. A country carries **one or more
 * locales**, because a market can be served in several languages (Canada, `EN`/`FR`)
 * — the country is the row, the locale is the destination.
 *
 * **Every locale the platform serves, not only nexus's.** The picker is the visitor's
 * way out of the wrong market, and eight of the ten markets are still rendered by the
 * legacy app; offering only the two nexus renders would strand everyone else. Which
 * app answers a given destination is the edge's decision, not this module's — see
 * `localeHref`. The table is the platform's markets; which of them a given page offers
 * is `offeredLocales` — today every offered page offers all of them.
 *
 * Country names are **endonyms** — each country's name in its own language, so
 * `México` and `United States` read the same whichever locale is active. They are
 * proper nouns, so they stay out of the translation store for the same reason
 * carrier names do (`menu-config.ts`): a translator handed "United States"
 * would be asked to translate a name, not prose. ui-react's `countries.json`
 * (`packages/wanderu-component-header/src/LocaleMenu/config/countries.json`) is the
 * surface this mirrors, name for name.
 */
interface Country {
    /** ISO 3166-1 alpha-2, lowercased — also the flag asset's basename. */
    countryCode: string
    /** The country's name in its own language. Never translated. */
    countryName: string
    /**
     * The locales nexus or legacy serves this country in, in the order the row offers
     * them. The first is the country's default — the one ui-react's whole-row press
     * resolves to.
     */
    locales: readonly PlatformLocale[]
}

const COUNTRIES: readonly Country[] = [
    { countryCode: 'ca', countryName: 'Canada', locales: ['en-ca', 'fr-ca'] },
    { countryCode: 'de', countryName: 'Deutschland', locales: ['de-de'] },
    { countryCode: 'es', countryName: 'España', locales: ['es-es'] },
    { countryCode: 'fr', countryName: 'France', locales: ['fr-fr'] },
    { countryCode: 'it', countryName: 'Italia', locales: ['it-it'] },
    { countryCode: 'mx', countryName: 'México', locales: ['es-mx'] },
    { countryCode: 'nl', countryName: 'Nederland', locales: ['nl-nl'] },
    { countryCode: 'gb', countryName: 'United Kingdom', locales: ['en-gb'] },
    { countryCode: 'us', countryName: 'United States', locales: ['en-us'] },
]

/**
 * Language **endonyms**, keyed by the locale's language subtag — the accessible name
 * of a language link, so a screen reader announces "Canada, Français" rather than
 * "Canada, F R". Endonyms for the same reason country names are, and static
 * rather than `Intl.DisplayNames`-derived: that API's output varies by ICU build, so
 * a server-rendered name could differ from the client's and break hydration
 * (`invariants.md#ssr-safe-rendered-dates` is the same hazard for dates).
 */
const LANGUAGE_NAMES: Record<string, string> = {
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
    fr: 'Français',
    it: 'Italiano',
    nl: 'Nederlands',
}

/**
 * The 4x3 flag for a country. Sourced from `flag-icons` (MIT) via ui-react's
 * `wanderu-flag-icons`, so the picker shows the same artwork as the legacy header —
 * see `LICENSE.third-party.md`.
 */
export function flagSrc(countryCode: string): string {
    return `/next-static/flags/${countryCode}.svg`
}

/** One of a country's languages, as a destination the row can offer. */
export interface LanguageOption {
    /** The locale this destination switches to. */
    locale: PlatformLocale
    /** The language subtag, uppercased — the chip's visible label (`EN`, `FR`). */
    label: string
    /** The language's endonym, for the link's accessible name. */
    languageName: string
    /** The destination — a final URL, never one that redirects. See `localeHref`. */
    href: string
    /** Whether this is the locale the visitor is already reading. */
    isActive: boolean
}

/** A country as the picker renders it: the flag, the name, and where it goes. */
export interface LocaleOption {
    /** ISO 3166-1 alpha-2, lowercased — also the flag asset's basename. */
    countryCode: string
    /** The country's name in its own language. Never translated. */
    countryName: string
    /** Flag asset path (`flagSrc`). */
    flag: string
    /**
     * This country's destinations, one per language it is served in. A single entry
     * makes the whole row one link; several make the row a name and a link per
     * language.
     */
    languages: LanguageOption[]
    /** Whether this is the country the visitor is already in. */
    isActive: boolean
}

/** The per-trip review's base segment — `/travel-review/<tripId>/`. */
const TRAVEL_REVIEW_SEGMENT = 'travel-review'

/** Non-empty path segments of a locale-less path — `/bus/us-ny/…` → `['bus','us-ny',…]`. */
function segmentsOf(pathname: string): string[] {
    return pathname.split(/[?#]/)[0].split('/').filter(Boolean)
}

/**
 * The locales `pathname` (**locale-less**, as `usePathname` from `@/i18n/navigation`
 * reports it) is worth offering — the ones where naming another country is a real
 * choice rather than a way of leaving the page. Empty everywhere else. Three shapes
 * qualify:
 *
 * - the **homepage**, which every locale has (its destination is resolved through
 *   `localeHomeUrl`, since the default locale's is the bare `/` and `/en-us/` `301`s);
 * - a **C2C route** — the five-segment `/mode/fromRegion/fromCity/toRegion/toCity/`,
 *   matched on the route *shape* against the mode vocabulary (`TRANS_MODES`) rather
 *   than a mode word anywhere in the path, and by exact length, so `/bus/` and
 *   `/bus/a/b/c/d/e/` are not C2C pages;
 * - a **travel review** — `/travel-review/<tripId>/`, exactly two segments, since
 *   `/travel-review/` alone is not a page and neither is a third segment under it.
 *
 * All three offer **every market**. The review looks like a nexus-only page and is not:
 * the legacy app carries it too, from one locale-agnostic route
 * (`wanderu-app-product-review/src/routes.jsx`) translated at runtime. On prod,
 * `/de-de/`, `/fr-fr/`, `/nl-nl/` and `/en-ca/` each return a working localized form,
 * not the `404` that would make the destination unsanctionable
 * (`invariants.md#rendered-links-are-canonical`). Which origin answers which prefix is
 * the edge's call and differs per environment, so it is deliberately not encoded here:
 * the destination is a relative path, as it is for every other offered page.
 *
 * An allowlist, not a list of exclusions, and that direction is the point: a new page
 * defaults to **no picker**, which is wrong-but-harmless, where the excluding form
 * defaults to offering a country whose page may `301`, `307` or `404` — and a `404` is
 * never sanctioned (`invariants.md#rendered-links-are-canonical`).
 *
 * The homepage and the C2C route are **fully determined by their path**. The review is
 * not — its per-customer `token`/`email` and trip context arrive as query parameters —
 * so it is offered only because the query travels with the path to every destination
 * (`localeHref`). A surface whose state lives somewhere the picker cannot carry
 * still gets nothing.
 *
 * Everything else is offered nothing. The classes that excludes, and why each one is
 * not a locale switch:
 *
 * - a **locale-free surface** (`/blog/…`, `/social`) — one URL shared by every
 *   country, so there is no other country's copy of it;
 * - a **proxy-coerced single-locale route** (`about-us`, `partners`, the legal pages)
 *   — the other locale's form `307`s straight back to the serving one, so the control
 *   reads as a no-op;
 * - an **en-us-only route that `404`s elsewhere** (`70mm-imax-theaters`,
 *   `airport-shuttles`, `world-cup-2026`) — the page refuses the locale and nothing
 *   rescues the request. The allowlist is what keeps these off the picker, and the
 *   drift guard in `locale-menu-config.test.ts` walks the route tree for that
 *   `notFound()`-on-locale shape, so allowlisting one is a failing build rather than a
 *   `404` nobody notices;
 * - a **both-locales page deliberately left off** — the framework's `nested` scaffold
 *   demo, which no visitor is routed to.
 *
 * What no path-only rule catches is a well-shaped path whose page nonetheless `404`s —
 * an unknown `tripId`, or a C2C route the store does not serve. Those render the shell,
 * and so a picker, at a URL whose counterpart 404s too — accepted, not guarded against.
 */
function offeredLocales(pathname: string): readonly PlatformLocale[] {
    const segments = segmentsOf(pathname)
    const [base] = segments

    // No segments at all is the homepage, which every locale has.
    if (base === undefined) {
        return hreflangLocales
    }
    if (base === TRAVEL_REVIEW_SEGMENT) {
        return segments.length === 2 ? hreflangLocales : []
    }
    if (segments.length === 5 && (TRANS_MODES as readonly string[]).includes(base)) {
        return hreflangLocales
    }
    return []
}

/**
 * Where picking `locale` sends a visitor who is on `pathname` (the **locale-less**
 * path, as `usePathname` from `@/i18n/navigation` reports it) with `search` (the query
 * string without its `?`, as `useSearchParams().toString()` returns it).
 *
 * The same path in the target locale, so the visitor keeps their place — except the
 * **homepage**, whose default-locale form is the bare `/` (`/en-us/` `301`s, and a
 * rendered link that redirects is `invariants.md#rendered-links-are-canonical`). Going
 * through `localeHomeUrl` keeps that in step with the brand logo, which answers the
 * same question.
 *
 * **A locale nexus does not serve is prefixed exactly like one it does.** The URL is
 * relative, so the *edge* resolves it: nexus only ever receives the paths the edge
 * routes to it, and `/de-de/…` is not among them, so the legacy app answers without
 * this module hard-coding a legacy host it would then have to keep per environment.
 * The picker resolves its destination and verifies nothing about it.
 *
 * **The query travels with the path**, because it is part of the page the visitor is
 * on — and on the travel review it *is* the page: the form's required `token` and
 * `email` arrive there, so a destination without them renders the form and then rejects
 * every submission. Nothing that must *not* cross a country switch reaches a
 * rendered nexus URL to begin with — the proxy strips the attribution params into
 * cookies with a `302`, and a `?cur=` request is routed to the legacy origin rather
 * than served here.
 *
 * Only ever asked about a path with a per-locale counterpart: `buildLocaleOptions`
 * offers no country anywhere else, so no other shape reaches here (`offeredLocales`).
 */
export function localeHref(locale: PlatformLocale, pathname: string, search?: string): string {
    const path = pathname === '/' ? localeHomeUrl(locale) : `/${locale}${pathname}`
    return search ? `${path}?${search}` : path
}

/**
 * The picker's options for a visitor in `activeLocale` on `pathname`, ordered by
 * country name — ui-react sorts the same way (`localeCompare(_, 'en')`), so the two
 * headers list the same countries in the same order.
 *
 * **No countries at all** on a page with no per-locale counterpart, and only the
 * countries whose language the page is served in where the offered set is narrower than
 * the platform's (`offeredLocales`). Returning none reaches the shell's own fewer-than-two gate
 * (`LocaleMenu`, `AppSidebar`), so no picker renders — the honest answer where every
 * destination we could name would take the reader off the page they are on without
 * giving them a translation of it.
 *
 * **The compiler is what keeps the country table honest**, not a runtime filter. Every
 * entry's locales are typed `PlatformLocale`, which *is* the served-locale list, so
 * retiring a locale there turns the country that claims it into a type error — the
 * `COUNTRIES` edit is forced, loudly, at build time. A `.filter()` against the same
 * list would only look like a guard: given that type it can never drop an element, and
 * a test asserting it would be true by construction.
 *
 * `search` is the current query string without its `?`; it travels to every destination
 * (`localeHref`). React-free like the rest of this directory's config — the caller reads
 * the path and the query out of the framework — so it unit-tests directly.
 */
export function buildLocaleOptions(
    activeLocale: PlatformLocale,
    pathname: string,
    search?: string,
): LocaleOption[] {
    const offered = offeredLocales(pathname)

    return (
        COUNTRIES.map((country) => {
            const languages = country.locales
                .filter((locale) => offered.includes(locale))
                .map((locale) => {
                    const language = locale.split('-')[0]
                    return {
                        locale,
                        label: language.toUpperCase(),
                        languageName: LANGUAGE_NAMES[language] ?? language,
                        href: localeHref(locale, pathname, search),
                        isActive: locale === activeLocale,
                    }
                })

            return {
                countryCode: country.countryCode,
                countryName: country.countryName,
                flag: flagSrc(country.countryCode),
                languages,
                // Read off the languages the row actually offers, not the country's
                // whole locale list: a row is the visitor's own only if the destination
                // they are already on is one of the ones it renders. Identical while
                // every offered set is the whole platform, and the guard that keeps a
                // "current" mark off a row that does not carry the current locale the
                // day some page offers a subset again.
                isActive: languages.some((language) => language.isActive),
            }
        })
            // A country with no offered language is not a row — dropping it here is what
            // turns a narrower offered set into a shorter list rather than a dead flag.
            .filter((country) => country.languages.length > 0)
            .sort((a, b) => a.countryName.localeCompare(b.countryName, 'en'))
    )
}

/**
 * The option the trigger shows. Total by construction — `activeLocale` is a served
 * locale and every served locale has a country — but a caller still gets `undefined`
 * rather than a throw if that ever stops holding, so the header degrades to no
 * picker instead of a blank page.
 */
export function activeLocaleOption(options: LocaleOption[]): LocaleOption | undefined {
    return options.find((option) => option.isActive)
}
